import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Sparkles, TrendingUp, Users, Wrench, DollarSign, Lightbulb, ChevronRight, Info } from "lucide-react";
import { SeatPersonalization } from "@/hooks/useSeatPersonalization";
import { SelectionGridSkeleton } from "./skeletons/SelectionCardSkeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DecisionPortalProps {
  personalization: SeatPersonalization;
  firstName?: string | null;
  onStartDecision: (decisionType: string) => void;
}

const DECISION_AREAS = [
  { id: "strategy", label: "Strategie & Positionierung", icon: Sparkles },
  { id: "product", label: "Produkt & Innovation", icon: Lightbulb },
  { id: "growth", label: "Wachstum & Skalierung", icon: TrendingUp },
  { id: "team", label: "Team & Führung", icon: Users },
  { id: "operations", label: "Operations & Prozesse", icon: Wrench },
  { id: "finance", label: "Finanzen & Investment", icon: DollarSign },
];

export const DecisionPortal = ({ personalization, firstName, onStartDecision }: DecisionPortalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 150);
    return () => clearTimeout(timer);
  }, []);

  // Prioritize preferred areas first, then show remaining areas
  const preferredAreas = DECISION_AREAS.filter(area =>
    personalization.preferred_decision_areas.includes(area.id)
  );
  const otherAreas = DECISION_AREAS.filter(area =>
    !personalization.preferred_decision_areas.includes(area.id)
  );

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
            Willkommen zurück
          </h1>
        </div>
        <SelectionGridSkeleton count={6} columns={2} />
      </div>
    );
  }

  const renderAreaCard = (area: typeof DECISION_AREAS[0], isPriority: boolean) => {
    const Icon = area.icon;
    const isHovered = hoveredArea === area.id;
    
    return (
      <button
        key={area.id}
        onClick={() => onStartDecision(area.id)}
        onMouseEnter={() => setHoveredArea(area.id)}
        onMouseLeave={() => setHoveredArea(null)}
        className={cn(
          "group relative flex items-center gap-4 rounded-xl border p-5 transition-all duration-300 text-left overflow-hidden",
          "bg-card/50 backdrop-blur-sm",
          "hover:shadow-md hover:-translate-y-0.5",
          isPriority 
            ? "border-primary/30 bg-primary/[0.03]" 
            : "border-border/60",
          isHovered && "border-primary/50 bg-card"
        )}
      >
        {/* Subtle gradient background on hover */}
        <div className={cn(
          "absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 opacity-0 transition-opacity duration-300",
          isHovered && "opacity-100"
        )} />
        
        <div className={cn(
          "relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-all duration-300",
          isPriority 
            ? "bg-primary/10 text-primary" 
            : "bg-muted/50 text-muted-foreground",
          isHovered && "bg-primary text-primary-foreground scale-105"
        )}>
          <Icon className="h-5 w-5" />
        </div>
        
        <div className="relative flex-1 min-w-0">
          <p className="font-semibold text-base leading-tight text-foreground">{area.label}</p>
          {isPriority && (
            <p className="text-xs text-muted-foreground mt-1">Dein bevorzugter Bereich</p>
          )}
        </div>
        
        <ChevronRight className={cn(
          "relative h-5 w-5 text-muted-foreground/50 transition-all duration-300",
          isHovered && "text-primary translate-x-1"
        )} />
      </button>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-3">
        <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
          Willkommen zurück{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-base text-muted-foreground">
          Welche Entscheidung möchtest du heute strukturiert durchdenken?
        </p>
      </div>

      {/* Inline hint with info popover */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Deine Auswahl definiert den Entscheidungs-Track.</span>
        <Popover>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center justify-center rounded-full p-1 hover:bg-muted/60 transition-colors">
              <Info className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
              <span className="sr-only">Mehr erfahren</span>
            </button>
          </PopoverTrigger>
          <PopoverContent 
            side="bottom" 
            align="start" 
            className="w-72 p-4"
          >
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">Was ändert sich?</p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                  Fragen
                </span>
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                  Analyse
                </span>
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                  Report
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Jeder Bereich aktiviert spezifische Fragen, fokussiert die KI-Analyse und strukturiert den finalen Report nach passenden Standards.
              </p>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Preferred Areas - Quick Access */}
      {preferredAreas.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-1 w-1 rounded-full bg-primary" />
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">Deine bevorzugten Bereiche</h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {preferredAreas.map((area) => renderAreaCard(area, true))}
          </div>
        </div>
      )}

      {/* All Other Areas */}
      {otherAreas.length > 0 && (
        <div className="space-y-4">
          {preferredAreas.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-1 w-1 rounded-full bg-muted-foreground/30" />
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Weitere Bereiche</h2>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3">
            {otherAreas.map((area) => renderAreaCard(area, false))}
          </div>
        </div>
      )}
    </div>
  );
};
